import React, { useEffect } from "react";
import Select from "react-select";
import api from "@/services/api";

/**
 * Dropdown component that provides both native HTML select and react-select functionality
 * Supports dynamic option loading, dependency management, and form validation
 *
 * @param {Object} props - Component props
 * @param {Array} props.options - Array of available options {value, name}
 * @param {boolean} props.clear - Whether to show clear button (default: false)
 * @param {Object} props.properties - Current form properties/values
 * @param {string} props.property - The property key this dropdown manages
 * @param {Object} props.field - Field configuration object with validation rules
 * @param {boolean} props.numbervalue - Whether to parse value as integer (default: false)
 * @param {Function} props.updateProperties - Callback to update parent component properties
 * @param {boolean} props.boolean - Whether this is a boolean dropdown (true/false values)
 * @param {Function} props.updateQuestionProperties - Callback to update question-specific properties
 * @param {boolean} props.notSelectedDisabled - Whether "not selected" option is disabled (default: true)
 * @param {Array} props.errors - Array of error property names for validation styling
 * @param {string} props.notSelectedText - Text for "not selected" option (default: "-- Selecteer een artikel --")
 */
const Dropdown = ({
    options,
    clear = false,
    properties,
    property,
    field,
    numbervalue = false,
    updateProperties,
    boolean,
    updateQuestionProperties,
    notSelectedDisabled = true,
    errors = [],
    notSelectedText = "-- Selecteer een artikel --",
}) => {
    /**
     * Auto-select single option if it's a material field with only one choice
     * This effect runs once on component mount
     */
    useEffect(() => {
        if (
            options.length === 1 &&
            !properties[property] &&
            field &&
            field.hasOwnProperty("material") &&
            field.material
        ) {
            updateValue(options[0]);
        }
    }, []);

    /**
     * Handle value changes and manage side effects
     * Processes the selected value, updates properties, handles dependencies, and makes API requests
     *
     * @param {Object|null} selectedoption - The selected option object or null for clear
     */
    async function updateValue(selectedoption) {
        // Determine the actual value to store based on selection type
        let value = selectedoption
            ? boolean
                ? selectedoption // For boolean dropdowns, use the option directly
                : selectedoption.value // For regular dropdowns, extract the value
            : field.hasOwnProperty("nullvalue")
              ? field.nullvalue // Use configured null value if available
              : 0; // Default to 0

        // Clone properties object to avoid mutations
        let props = { ...properties };

        // Convert to integer if required
        if (numbervalue) {
            value = parseInt(value);
        }

        // Set the new value
        props[property] = value;

        // Reset dependent properties if value changed and reset dependency is configured
        if (
            value !== properties[property] &&
            field &&
            field.hasOwnProperty("resetdependency") &&
            field.resetdependency
        ) {
            if (Array.isArray(field.request_prop)) {
                // Reset multiple dependent properties
                field.request_prop.forEach((prop) => {
                    props[prop] = 0;
                });
            } else {
                // Reset single dependent property
                props[field.request_prop] = 0;
            }
        }

        // Make API request if field requires it
        if (field && field.request) {
            await api.get(field.request_url + "/" + value).then(
                (response) => {
                    // Update question properties with API response
                    if (Array.isArray(field.request_prop)) {
                        // Handle multiple request properties
                        field.request_prop.forEach((prop) => {
                            updateQuestionProperties(
                                response.data.products,
                                prop,
                            );
                        });
                    } else {
                        // Handle single request property
                        let products = response.data.products;
                        updateQuestionProperties(products, field.request_prop);
                    }

                    // Auto-select if API returns single product
                    if (
                        response.data.products &&
                        response.data.products.length === 1 &&
                        field.request_prop
                    ) {
                        if (Array.isArray(field.request_prop)) {
                            // Set value for multiple properties
                            field.request_prop.forEach((prop) => {
                                props[prop] =
                                    response.data.products[0]["value"];
                            });
                        } else {
                            // Set value for single property
                            props[field.request_prop] =
                                response.data.products[0]["value"];
                        }
                    } else {
                        // Reset to 0 if multiple or no products returned
                        props[field.request_prop] = 0;
                    }
                    updateProperties(props, property);
                },
            );
        } else {
            // No API request needed - just update properties
            props[property] = value;
            updateProperties(props, property);
        }
    }

    /**
     * Filter function for react-select search functionality
     * Performs case-insensitive search within option labels
     *
     * @param {Object} candidate - The option being evaluated
     * @param {string} input - The search input string
     * @returns {boolean} Whether the option matches the search
     */
    const filterOption = (candidate, input) => {
        return candidate.label.toLowerCase().includes(input.toLowerCase());
    };

    /**
     * Transform options array to react-select format
     * Converts {value, name} objects to {value, label} format
     *
     * @returns {Array} Array of options in react-select format
     */
    const listOptions = () => {
        return options.map((option) => {
            return { value: option.value, label: option.name };
        });
    };

    /**
     * Get the currently selected value in react-select format
     * Returns false for boolean dropdowns, filtered array for regular dropdowns
     *
     * @returns {boolean|Array} Current selection for react-select
     */
    const getSelectedValue = () => {
        if (boolean) {
            return false;
        }
        let options = listOptions();
        return options.filter((option) => {
            return option.value === properties[property];
        });
    };

    /**
     * Custom styles for react-select component
     * Includes error state styling with red border
     */
    const colourStyles = {
        control: (styles) => ({
            ...styles,
            backgroundColor: "white",
            borderColor: errors.includes(property) ? "red" : "#cccccc",
        }),
        menuPortal: (styles) => ({
            ...styles,
            zIndex: 9999,
        }),
    };

    return (
        <div className={"row forminput"}>
            <div className="col-12 input-group">
                <div className="w-100">
                    {boolean ? (
                        <div className="position-relative dropdown w-100">
                            <select
                                className="selectwhite"
                                disabled={
                                    field && field.hasOwnProperty("disabled")
                                        ? field.disabled
                                        : false
                                }
                                value={
                                    boolean
                                        ? properties[property]
                                            ? 1 // Convert true to 1
                                            : 0 // Convert false to 0
                                        : properties[property]
                                }
                                onChange={(e) =>
                                    updateValue(e.target.value, property)
                                }
                            >
                                {boolean ? (
                                    <></>
                                ) : (
                                    <option
                                        disabled={notSelectedDisabled}
                                        value="0"
                                    >
                                        {notSelectedText}
                                    </option>
                                )}
                                {options.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.name}
                                    </option>
                                ))}
                            </select>
                            <div className="pointer">
                                <svg
                                    className="fill-current"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                >
                                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                </svg>
                            </div>
                        </div>
                    ) : (
                        <Select
                            onChange={updateValue}
                            value={getSelectedValue()}
                            styles={colourStyles}
                            filterOption={filterOption}
                            options={listOptions()}
                            isClearable={clear || (field && field.clear)}
                            isDisabled={
                                field && field.hasOwnProperty("disabled")
                                    ? field.disabled
                                    : false
                            }
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dropdown;
